import { useEffect, useState } from "react";
import { NavLink, Route, Routes, useNavigate } from "react-router-dom";
import { cotAxios } from "./api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  LogOut, Users, Package, FileSpreadsheet, ShoppingCart, TrendingUp,
  Sparkles, Home, ShieldCheck, CreditCard, CheckCircle2,
} from "lucide-react";
import CotacaoFornecedoresPage from "./CotacaoFornecedoresPage";
import CotacaoProdutosPage from "./CotacaoProdutosPage";
import CotacaoListaPage from "./CotacaoListaPage";
import CotacaoNovaPage from "./CotacaoNovaPage";
import CotacaoDetalhePage from "./CotacaoDetalhePage";
import CotacaoMapaPage from "./CotacaoMapaPage";
import CotacaoPedidosPage from "./CotacaoPedidosPage";
import CotacaoDashHomePage from "./CotacaoDashHomePage";
import CotacaoAcessosPage from "./CotacaoAcessosPage";
import useCotacaoPwa from "./useCotacaoPwa";

function formatDataBR(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return "—"; }
}

function PlanoStatusCard({ user, onAssinar, loading }) {
  const ativa = !!user?.assinatura_ativa;
  return (
    <Card className={`mb-4 shadow-sm ${ativa ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="plano-status-card">
      <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          {ativa ? (
            <CheckCircle2 className="w-8 h-8 text-emerald-600 shrink-0" />
          ) : (
            <Sparkles className="w-8 h-8 text-amber-600 shrink-0" />
          )}
          <div>
            <div className={`font-bold text-sm ${ativa ? "text-emerald-800" : "text-amber-800"}`}>
              {ativa ? (user.plano_nome || "Cotações Pro") : "Sem assinatura ativa"}
            </div>
            <div className="text-xs text-slate-700">
              {ativa ? (
                <>Próxima renovação: <b>{formatDataBR(user.proxima_renovacao)}</b> · {Number(user.plano_valor || 497).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}/mês</>
              ) : (
                <>Assine o Plano Único · <b>R$ 497/mês</b> · Cancele quando quiser</>
              )}
            </div>
          </div>
        </div>
        {!ativa && (
          <Button
            onClick={onAssinar}
            disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            data-testid="btn-assine-ja"
          >
            <CreditCard className="w-4 h-4" />
            {loading ? "Abrindo..." : "Assine Já"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function HomeInicio({ user, onAssinar, assinaturaLoading }) {
  const tiles = [
    { to: "/cotacoes/dashboard/fornecedores", icon: Users, title: "Fornecedores", desc: "Cadastro completo, histórico e ranking.", ativo: true },
    { to: "/cotacoes/dashboard/produtos", icon: Package, title: "Produtos", desc: "Código, marca, referência e unidade.", ativo: true },
    { to: "/cotacoes/dashboard/cotacoes/nova", icon: FileSpreadsheet, title: "Nova Cotação", desc: "Selecione produtos e fornecedores.", ativo: true },
    { to: "/cotacoes/dashboard/cotacoes", icon: ShoppingCart, title: "Mapa de Cotações", desc: "Melhores propostas em verde, aprove por item.", ativo: true },
    { to: "/cotacoes/dashboard/pedidos", icon: FileSpreadsheet, title: "Pedidos gerados", desc: "Histórico de pedidos com PDF.", ativo: true },
    { to: "/cotacoes/dashboard/analytics", icon: TrendingUp, title: "Dashboard mensal", desc: "Valor total, rankings e evolução.", ativo: true },
  ];

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <PlanoStatusCard user={user} onAssinar={onAssinar} loading={assinaturaLoading} />
      <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">Painel</h1>
          <p className="text-slate-600 text-sm">
            Bem-vindo, <b>{user?.nome}</b>. Comece cadastrando seus fornecedores e produtos.
          </p>
        </div>
        {user?.super_admin && (
          <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-medium">
            <ShieldCheck className="w-3 h-3" /> Super Admin
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tiles.map((t, i) => {
          const inner = (
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <t.icon className="w-5 h-5 text-blue-700" />
                </div>
                {t.tag && <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded bg-amber-100 text-amber-700">{t.tag}</span>}
                {t.ativo && <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-1 rounded bg-emerald-100 text-emerald-700">Ativo</span>}
              </div>
              <div className="font-semibold">{t.title}</div>
              <div className="text-sm text-slate-600 mt-1">{t.desc}</div>
            </CardContent>
          );
          if (t.to) {
            return (
              <NavLink to={t.to} key={i} data-testid={`home-tile-${t.title.toLowerCase()}`}>
                <Card className="border-slate-200 hover:border-blue-400 transition cursor-pointer h-full">{inner}</Card>
              </NavLink>
            );
          }
          return (
            <Card key={i} className="border-slate-200 opacity-70 cursor-not-allowed">{inner}</Card>
          );
        })}
      </div>
    </div>
  );
}

export default function CotacaoDashboard() {
  useCotacaoPwa();
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [assinaturaLoading, setAssinaturaLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("cotacao_token");
    if (!token) { nav("/cotacoes/login"); return; }
    cotAxios.get("/cotacao/auth/me")
      .then(({ data }) => {
        setUser(data);
        localStorage.setItem("cotacao_user", JSON.stringify(data));
      })
      .catch(() => {
        localStorage.removeItem("cotacao_token");
        localStorage.removeItem("cotacao_user");
        nav("/cotacoes/login");
      });
  }, [nav]);

  const handleAssinar = async () => {
    setAssinaturaLoading(true);
    try {
      const { data } = await cotAxios.post("/cotacao/checkout", {});
      if (data.status === "checkout_criado" && data.init_point) {
        window.location.href = data.init_point;
        return;
      }
      if (data.status === "em_breve" || data.status === "erro_mp") {
        toast.error(data.message || "Checkout indisponível no momento");
        if (data.whatsapp) window.open(data.whatsapp, "_blank");
        return;
      }
      toast.error("Resposta inesperada do checkout");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao iniciar checkout");
    } finally {
      setAssinaturaLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("cotacao_token");
    localStorage.removeItem("cotacao_user");
    toast.success("Você saiu.");
    nav("/cotacoes");
  };

  if (!user) return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando...</div>;

  const linkCls = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition ${
      isActive ? "bg-blue-100 text-blue-800" : "text-slate-700 hover:bg-slate-100"
    }`;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="cotacao-dashboard">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-compromisso.png" alt="CD" className="w-10 h-10 rounded-xl" />
            <div>
              <div className="font-bold" style={{ fontFamily: "Work Sans" }}>Sistema de Cotações</div>
              <div className="text-xs text-slate-500 flex items-center gap-1 flex-wrap">
                {user.email}
                {user.tipo && user.tipo !== "admin" && !user.super_admin && (
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${user.tipo === "comprador" ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-700"}`} data-testid="badge-role">
                    {user.tipo.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={logout} data-testid="btn-logout">
            <LogOut className="w-4 h-4 mr-1" /> Sair
          </Button>
        </div>
        {/* Nav horizontal */}
        <nav className="max-w-7xl mx-auto px-4 pb-2 flex gap-1 overflow-x-auto">
          <NavLink to="/cotacoes/dashboard" end className={linkCls} data-testid="nav-home">
            <Home className="w-4 h-4" /> Início
          </NavLink>
          <NavLink to="/cotacoes/dashboard/fornecedores" className={linkCls} data-testid="nav-fornecedores">
            <Users className="w-4 h-4" /> Fornecedores
          </NavLink>
          <NavLink to="/cotacoes/dashboard/produtos" className={linkCls} data-testid="nav-produtos">
            <Package className="w-4 h-4" /> Produtos
          </NavLink>
          <NavLink to="/cotacoes/dashboard/cotacoes" className={linkCls} data-testid="nav-cotacoes">
            <FileSpreadsheet className="w-4 h-4" /> Cotações
          </NavLink>
          <NavLink to="/cotacoes/dashboard/pedidos" className={linkCls} data-testid="nav-pedidos">
            <ShoppingCart className="w-4 h-4" /> Pedidos
          </NavLink>
          <NavLink to="/cotacoes/dashboard/analytics" className={linkCls} data-testid="nav-analytics">
            <TrendingUp className="w-4 h-4" /> Analytics
          </NavLink>
          {(user.tipo === "admin" || user.super_admin) && (
            <NavLink to="/cotacoes/dashboard/acessos" className={linkCls} data-testid="nav-acessos">
              <ShieldCheck className="w-4 h-4" /> Acessos
            </NavLink>
          )}
        </nav>
      </header>

      <main>
        <Routes>
          <Route index element={<HomeInicio user={user} onAssinar={handleAssinar} assinaturaLoading={assinaturaLoading} />} />
          <Route path="fornecedores" element={<CotacaoFornecedoresPage />} />
          <Route path="produtos" element={<CotacaoProdutosPage />} />
          <Route path="cotacoes" element={<CotacaoListaPage />} />
          <Route path="cotacoes/nova" element={<CotacaoNovaPage />} />
          <Route path="cotacoes/:id" element={<CotacaoDetalhePage />} />
          <Route path="cotacoes/:id/mapa" element={<CotacaoMapaPage />} />
          <Route path="pedidos" element={<CotacaoPedidosPage />} />
          <Route path="analytics" element={<CotacaoDashHomePage />} />
          <Route path="acessos" element={<CotacaoAcessosPage />} />
        </Routes>
      </main>
    </div>
  );
}
